import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, Youtube, Mail, Phone, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSite } from "@/contexts/SiteContext";
import { trackEvent } from "@/components/Analytics";

export default function Footer() {
  const { t } = useTranslation();
  const { company, contact, social } = useSite();

  return (
    <footer className="bg-atelier-deep border-t atelier-border-soft mt-auto relative z-10">
      <div className="px-5 lg:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              {company?.logo_url && (
                <img src={company.logo_url} alt="AMAR SAHRAOUI" className="h-12 w-auto" style={{ mixBlendMode: "screen" }} />
              )}
              <div className="leading-none">
                <div className="font-serif text-lg tracking-[0.2em] uppercase text-atelier-ink">AMAR SAHRAOUI</div>
                <div className="label-tech text-[0.55rem] text-atelier-textFaint mt-1">Promotion Immobilière</div>
              </div>
            </div>
            <p className="font-serif italic text-base text-atelier-textDim max-w-md leading-relaxed">
              {company?.slogan || "Votre chez-soi vous attend, conçu avec soin, bâti pour votre sérénité."}
            </p>
            <div className="mt-8 flex gap-2">
              {social?.facebook && (
                <a href={social.facebook} target="_blank" rel="noreferrer" className="w-10 h-10 atelier-border flex items-center justify-center text-atelier-textDim hover:text-atelier-ink hover:border-atelier-ink transition-all duration-300" data-testid="footer-social-facebook">
                  <Facebook className="w-4 h-4" strokeWidth={1.4} />
                </a>
              )}
              {social?.instagram && (
                <a href={social.instagram} target="_blank" rel="noreferrer" className="w-10 h-10 atelier-border flex items-center justify-center text-atelier-textDim hover:text-atelier-ink hover:border-atelier-ink transition-all duration-300" data-testid="footer-social-instagram">
                  <Instagram className="w-4 h-4" strokeWidth={1.4} />
                </a>
              )}
              {social?.linkedin && (
                <a href={social.linkedin} target="_blank" rel="noreferrer" className="w-10 h-10 atelier-border flex items-center justify-center text-atelier-textDim hover:text-atelier-ink hover:border-atelier-ink transition-all duration-300" data-testid="footer-social-linkedin">
                  <Linkedin className="w-4 h-4" strokeWidth={1.4} />
                </a>
              )}
              {social?.youtube && (
                <a href={social.youtube} target="_blank" rel="noreferrer" className="w-10 h-10 atelier-border flex items-center justify-center text-atelier-textDim hover:text-atelier-ink hover:border-atelier-ink transition-all duration-300">
                  <Youtube className="w-4 h-4" strokeWidth={1.4} />
                </a>
              )}
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="label-tech text-atelier-ink mb-5">{t("footer.nav")}</div>
            <ul className="space-y-3 text-sm text-atelier-textDim">
              <li><Link to="/" className="hover:text-atelier-ink transition-colors">{t("nav.home")}</Link></li>
              <li><Link to="/societe" className="hover:text-atelier-ink transition-colors">{t("nav.about")}</Link></li>
              <li><Link to="/prestations" className="hover:text-atelier-ink transition-colors">{t("nav.services")}</Link></li>
              <li><Link to="/projets" className="hover:text-atelier-ink transition-colors">{t("nav.projects")}</Link></li>
              <li><Link to="/disponibilites" className="hover:text-atelier-ink transition-colors">{t("nav.availabilities")}</Link></li>
              <li><Link to="/galerie" className="hover:text-atelier-ink transition-colors">{t("nav.gallery")}</Link></li>
              <li><Link to="/contact" className="hover:text-atelier-ink transition-colors">{t("nav.contact")}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="label-tech text-atelier-ink mb-5">{t("footer.contact")}</div>
            <ul className="space-y-4 text-sm text-atelier-textDim">
              {contact?.address && (
                <li className="flex gap-3"><MapPin className="w-4 h-4 mt-0.5 text-atelier-ink flex-shrink-0" strokeWidth={1.4} /><span>{contact.address}</span></li>
              )}
              {contact?.phone && (
                <li className="flex gap-3"><Phone className="w-4 h-4 mt-0.5 text-atelier-ink flex-shrink-0" strokeWidth={1.4} /><a href={`tel:${contact.phone.replace(/\s/g, "")}`} onClick={() => trackEvent("phone_click", { location: "footer" })} className="hover:text-atelier-ink transition-colors">{contact.phone}</a></li>
              )}
              {contact?.email && (
                <li className="flex gap-3"><Mail className="w-4 h-4 mt-0.5 text-atelier-ink flex-shrink-0" strokeWidth={1.4} /><a href={`mailto:${contact.email}`} onClick={() => trackEvent("email_click", { location: "footer" })} className="hover:text-atelier-ink transition-colors break-all">{contact.email}</a></li>
              )}
              {contact?.whatsapp && (
                <li className="flex gap-3 items-start">
                  <span className="label-tech text-[0.65rem] mt-1 text-atelier-ink flex-shrink-0">WA</span>
                  <a href={`https://wa.me/${contact.whatsapp.replace(/\D/g, "")}`} onClick={() => trackEvent("whatsapp_click", { location: "footer" })} target="_blank" rel="noreferrer" className="hover:text-atelier-ink transition-colors">{contact.whatsapp}</a>
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t atelier-border-soft flex flex-col md:flex-row justify-between items-start md:items-center gap-4 label-tech text-[0.62rem] text-atelier-textFaint">
          <div>© {new Date().getFullYear()} AMAR SAHRAOUI — {t("footer.rights")}</div>
          <div className="flex flex-wrap gap-6">
            <Link to="/legal/mentions-legales" className="hover:text-atelier-ink transition-colors">{t("footer.legal")}</Link>
            <Link to="/legal/politique-confidentialite" className="hover:text-atelier-ink transition-colors">{t("footer.privacy")}</Link>
            <Link to="/admin/login" className="opacity-40 hover:opacity-100 hover:text-atelier-ink transition-all" data-testid="footer-admin-link">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
